import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import AppModal from "../../components/AppModal";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const ACTIONS = [
	{ id: "pending", labelKey: "actionPending" },
	{ id: "close", labelKey: "actionClose" },
	{ id: "change_queue", labelKey: "actionChangeQueue" },
	{ id: "transfer_user", labelKey: "actionTransferUser" },
];

function minutesToHours(minutes) {
	const n = Number(minutes);
	if (!Number.isFinite(n) || n <= 0) return 2;
	const hours = n / 60;
	return Number.isInteger(hours) ? hours : Math.round(hours * 100) / 100;
}

function hoursToMinutes(hours) {
	const n = Number(hours);
	if (!Number.isFinite(n) || n <= 0) return 60;
	return Math.max(1, Math.round(n * 60));
}

function actionLabel(action) {
	const found = ACTIONS.find((item) => item.id === action);
	return found ? i18n.t(`followUp.${found.labelKey}`) : action || "—";
}

function emptyForm(item) {
	return {
		enabled: Boolean(item?.enabled),
		body: item?.body || "",
		messageDelayHours: minutesToHours(item?.messageDelayMinutes),
		actionDelayHours: minutesToHours(item?.actionDelayMinutes),
		action: item?.action || "pending",
		targetQueueId: item?.targetQueueId || "",
		targetUserId: item?.targetUserId || "",
		sendFarewell: Boolean(item?.sendFarewell),
		tagIds: Array.isArray(item?.tagIds) ? item.tagIds.map(Number) : [],
	};
}

function IconClock() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	);
}

export default function FollowUp() {
	const [items, setItems] = useState([]);
	const [users, setUsers] = useState([]);
	const [tags, setTags] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [selected, setSelected] = useState(null);
	const [form, setForm] = useState(emptyForm());

	const loadFollowUps = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await apiV2.get("/follow-ups");
			setItems(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, []);

	const loadUsers = useCallback(async () => {
		try {
			const collected = [];
			let pageNumber = 1;
			let hasMore = true;
			while (hasMore && pageNumber <= 10) {
				const { data } = await api.get("/users/", {
					params: { pageNumber },
				});
				const page = Array.isArray(data?.users) ? data.users : [];
				collected.push(...page);
				hasMore = Boolean(data?.hasMore);
				pageNumber += 1;
			}
			setUsers(collected);
		} catch (err) {
			toastError(err);
		}
	}, []);

	const loadTags = useCallback(async () => {
		try {
			const { data } = await api.get("/tags/list");
			setTags(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		loadFollowUps();
		loadUsers();
		loadTags();
	}, [loadFollowUps, loadUsers, loadTags]);

	const activeCount = useMemo(
		() => items.filter((item) => item.enabled).length,
		[items]
	);

	const openEditor = (item) => {
		setSelected(item);
		setForm(emptyForm(item));
	};

	const closeEditor = () => {
		if (saving) return;
		setSelected(null);
	};

	const handleSave = async () => {
		if (!selected) return;
		if (form.enabled && !String(form.body || "").trim()) {
			toast.error(i18n.t("followUp.errors.bodyRequired"));
			return;
		}
		if (form.enabled && form.action === "change_queue" && !form.targetQueueId) {
			toast.error(i18n.t("followUp.errors.queueRequired"));
			return;
		}
		if (form.enabled && form.action === "transfer_user" && !form.targetUserId) {
			toast.error(i18n.t("followUp.errors.userRequired"));
			return;
		}

		setSaving(true);
		try {
			const payload = {
				enabled: Boolean(form.enabled),
				body: String(form.body || "").trim(),
				messageDelayMinutes: hoursToMinutes(form.messageDelayHours),
				actionDelayMinutes: hoursToMinutes(form.actionDelayHours),
				action: form.action,
				targetQueueId: form.targetQueueId ? Number(form.targetQueueId) : 0,
				targetUserId: form.targetUserId ? Number(form.targetUserId) : 0,
				sendFarewell: form.action === "close" ? Boolean(form.sendFarewell) : false,
				tagIds: Array.isArray(form.tagIds) ? form.tagIds.map(Number).filter(Boolean) : [],
			};
			const { data } = await apiV2.put(`/follow-ups/${selected.queueId}`, payload);
			setItems((current) =>
				current.map((item) =>
					item.queueId === selected.queueId ? { ...item, ...data } : item
				)
			);
			toast.success(i18n.t("followUp.toasts.saved"));
			setSelected(null);
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<AppPageTitle
						title={i18n.t("followUp.title")}
						subtitle={i18n.t("followUp.adminSubtitle")}
					/>

					<div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
						<div className="card">
							<div className="card-body flex items-start justify-between gap-4">
								<div>
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500">
										{i18n.t("followUp.summary.active")}
									</p>
									<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
										{activeCount}
									</p>
									<p className="mt-2 mb-0 text-sm text-default-500">
										{i18n.t("followUp.summary.activeHint")}
									</p>
								</div>
								<div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
									<IconClock />
								</div>
							</div>
						</div>
						<div className="card">
							<div className="card-body">
								<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("followUp.summary.how")}
								</p>
								<p className="m-0 text-sm text-default-600 dark:text-default-300">
									{i18n.t("followUp.summary.howHint")}
								</p>
							</div>
						</div>
					</div>

					<section className="card">
						<div className="card-header px-5 pt-5 pb-4">
							<h6 className="card-title m-0">{i18n.t("followUp.listTitle")}</h6>
							<p className="mt-1 mb-0 text-sm text-default-500">
								{i18n.t("followUp.listHint")}
							</p>
						</div>
						<div className="card-body p-0">
							{loading ? (
								<p className="px-5 py-8 text-sm text-default-500">
									{i18n.t("followUp.loading")}
								</p>
							) : items.length === 0 ? (
								<p className="px-5 py-8 text-sm text-default-500">
									{i18n.t("followUp.empty")}
								</p>
							) : (
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
										<thead>
											<tr className="text-left text-xs font-semibold uppercase tracking-wide text-default-500">
												<th className="px-5 py-3">{i18n.t("followUp.table.queue")}</th>
												<th className="px-5 py-3">{i18n.t("followUp.table.status")}</th>
												<th className="px-5 py-3">{i18n.t("followUp.table.delays")}</th>
												<th className="px-5 py-3">{i18n.t("followUp.table.action")}</th>
												<th className="px-5 py-3 text-right">{i18n.t("followUp.table.actions")}</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-default-200 dark:divide-default-700">
											{items.map((item) => (
												<tr key={item.queueId} className="text-sm text-default-800 dark:text-default-200">
													<td className="px-5 py-3">
														<div className="flex items-center gap-2">
															<span
																className="size-3 rounded-full"
																style={{ backgroundColor: item.queueColor || "#94a3b8" }}
															/>
															<span className="font-medium">{item.queueName}</span>
														</div>
													</td>
													<td className="px-5 py-3">
														{item.enabled ? (
															<span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success">
																{i18n.t("followUp.enabled")}
															</span>
														) : (
															<span className="rounded-full bg-default-100 px-2 py-0.5 text-xs font-semibold text-default-500 dark:bg-default-800">
																{i18n.t("followUp.disabled")}
															</span>
														)}
													</td>
													<td className="px-5 py-3 text-default-600 dark:text-default-300">
														{minutesToHours(item.messageDelayMinutes)}h /{" "}
														{minutesToHours(item.actionDelayMinutes)}h
													</td>
													<td className="px-5 py-3">{actionLabel(item.action)}</td>
													<td className="px-5 py-3 text-right">
														<button
															type="button"
															className="btn btn-sm bg-primary text-white"
															onClick={() => openEditor(item)}
														>
															{i18n.t("followUp.buttons.configure")}
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</section>
				</div>
			</div>

			<AppModal
				open={Boolean(selected)}
				onClose={closeEditor}
				title={selected ? `${i18n.t("followUp.modalTitle")} · ${selected.queueName}` : ""}
				subtitle={i18n.t("followUp.modalHint")}
				maxWidth={640}
				footer={
					<div className="flex justify-end gap-2">
						<button
							type="button"
							className="btn btn-sub-gray"
							onClick={closeEditor}
							disabled={saving}
						>
							{i18n.t("followUp.buttons.cancel")}
						</button>
						<button
							type="button"
							className="btn bg-primary text-white"
							onClick={handleSave}
							disabled={saving}
						>
							{saving ? i18n.t("followUp.buttons.saving") : i18n.t("followUp.buttons.save")}
						</button>
					</div>
				}
			>
				{selected && (
					<div className="space-y-4">
						<label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-default-800 dark:text-default-100">
							<input
								type="checkbox"
								className="size-4 rounded border-default-300 text-primary focus:ring-primary"
								checked={Boolean(form.enabled)}
								onChange={(event) =>
									setForm((current) => ({ ...current, enabled: event.target.checked }))
								}
							/>
							{i18n.t("followUp.fields.enabled")}
						</label>

						<div>
							<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
								{i18n.t("followUp.fields.body")}
							</label>
							<textarea
								className="form-input w-full"
								rows={4}
								value={form.body}
								onChange={(event) =>
									setForm((current) => ({ ...current, body: event.target.value }))
								}
								placeholder={i18n.t("followUp.fields.bodyPlaceholder")}
							/>
						</div>

						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
									{i18n.t("followUp.fields.messageHours")}
								</label>
								<input
									type="number"
									min="0.02"
									step="0.25"
									className="form-input w-full"
									value={form.messageDelayHours}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											messageDelayHours: event.target.value,
										}))
									}
								/>
							</div>
							<div>
								<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
									{i18n.t("followUp.fields.actionHours")}
								</label>
								<input
									type="number"
									min="0.02"
									step="0.25"
									className="form-input w-full"
									value={form.actionDelayHours}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											actionDelayHours: event.target.value,
										}))
									}
								/>
							</div>
						</div>

						<div>
							<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
								{i18n.t("followUp.fields.action")}
							</label>
							<select
								className="form-select w-full"
								value={form.action}
								onChange={(event) =>
									setForm((current) => ({ ...current, action: event.target.value }))
								}
							>
								{ACTIONS.map((item) => (
									<option key={item.id} value={item.id}>
										{i18n.t(`followUp.${item.labelKey}`)}
									</option>
								))}
							</select>
						</div>

						{(form.action === "change_queue" || form.action === "transfer_user") && (
							<div>
								<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
									{i18n.t("followUp.fields.targetQueue")}
									{form.action === "change_queue" ? " *" : ""}
								</label>
								<select
									className="form-select w-full"
									value={form.targetQueueId}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											targetQueueId: event.target.value,
										}))
									}
								>
									<option value="">{i18n.t("followUp.fields.none")}</option>
									{items
										.filter((item) => item.queueId !== selected.queueId)
										.map((item) => (
											<option key={item.queueId} value={item.queueId}>
												{item.queueName}
											</option>
										))}
								</select>
							</div>
						)}

						{(form.action === "transfer_user" || form.action === "change_queue") && (
							<div>
								<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
									{i18n.t("followUp.fields.targetUser")}
									{form.action === "transfer_user" ? " *" : ""}
								</label>
								<select
									className="form-select w-full"
									value={form.targetUserId}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											targetUserId: event.target.value,
										}))
									}
								>
									<option value="">{i18n.t("followUp.fields.none")}</option>
									{users.map((item) => (
										<option key={item.id} value={item.id}>
											{item.name}
										</option>
									))}
								</select>
							</div>
						)}

						{form.action === "close" && (
							<div>
								<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
									{i18n.t("followUp.fields.closeMode")}
								</label>
								<div className="space-y-2">
									<label className="flex cursor-pointer items-start gap-2 text-sm text-default-800 dark:text-default-100">
										<input
											type="radio"
											className="mt-0.5"
											name="closeMode"
											checked={!form.sendFarewell}
											onChange={() =>
												setForm((current) => ({ ...current, sendFarewell: false }))
											}
										/>
										<span>
											<span className="font-medium">
												{i18n.t("followUp.fields.closeSilent")}
											</span>
											<span className="mt-0.5 block text-xs text-default-500">
												{i18n.t("followUp.fields.closeSilentHint")}
											</span>
										</span>
									</label>
									<label className="flex cursor-pointer items-start gap-2 text-sm text-default-800 dark:text-default-100">
										<input
											type="radio"
											className="mt-0.5"
											name="closeMode"
											checked={Boolean(form.sendFarewell)}
											onChange={() =>
												setForm((current) => ({ ...current, sendFarewell: true }))
											}
										/>
										<span>
											<span className="font-medium">
												{i18n.t("followUp.fields.closeFarewell")}
											</span>
											<span className="mt-0.5 block text-xs text-default-500">
												{i18n.t("followUp.fields.closeFarewellHint")}
											</span>
										</span>
									</label>
								</div>
							</div>
						)}

						<div>
							<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
								{i18n.t("followUp.fields.tags")}
							</label>
							<p className="mb-2 mt-0 text-xs text-default-500">
								{i18n.t("followUp.fields.tagsHint")}
							</p>
							{tags.length === 0 ? (
								<p className="m-0 text-sm text-default-500">
									{i18n.t("followUp.fields.tagsEmpty")}
								</p>
							) : (
								<div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-xl border border-default-200 p-3 dark:border-default-700">
									{tags.map((tag) => {
										const checked = form.tagIds.map(Number).includes(Number(tag.id));
										return (
											<label
												key={tag.id}
												className={`flex cursor-pointer items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium ${
													checked
														? "border-primary/40 bg-primary/10"
														: "border-default-200 bg-white dark:border-default-700 dark:bg-default-950"
												}`}
											>
												<input
													type="checkbox"
													className="size-3.5 rounded border-default-300 text-primary focus:ring-primary"
													checked={checked}
													onChange={() => {
														setForm((current) => {
															const currentIds = current.tagIds.map(Number);
															const id = Number(tag.id);
															const next = currentIds.includes(id)
																? currentIds.filter((item) => item !== id)
																: [...currentIds, id];
															return { ...current, tagIds: next };
														});
													}}
												/>
												<span
													className="size-2.5 rounded-full"
													style={{ backgroundColor: tag.color || "#94a3b8" }}
												/>
												{tag.name}
											</label>
										);
									})}
								</div>
							)}
						</div>
					</div>
				)}
			</AppModal>
		</MainContainer>
	);
}
